/* eslint-disable jsx-a11y/anchor-is-valid */
import moment from 'moment';
import React, { FunctionComponent, useCallback } from 'react';
import Loader from 'react-loader-spinner';
import { useDispatch, useSelector } from 'react-redux';
import { openDialog } from '../../../../app/dialog/dialog.actions';
import { pageStateSelectors } from '../../../../app/page/page-state/page-state.selectors';
import { updateUser } from '../../../../app/page/page-storage/users/users.actions';
import { userSelectors } from '../../../../app/resource/user/user.selectors';
import { IUser, IUserUpdateForm } from '../../../../app/resource/user/user.types';
import { useSelectWithParams } from '../../../../utils/selector.utils';
import { MeetingInfo } from './MeetingInfo';
import { IUserInfoParams } from './types';
import { CircularAvatar } from '../../common/CustomAvatar';
import { meetingSimpleSelectors } from '../../../../app/resource/meeting-simple/meeting-simple.selectors';
import WhiteShieldIcon from '../../../resources/images/white-shield.svg';
import { Link } from 'react-router-dom';
import CopyToClipboard from '../../common/CopyToClipboard';
import { authSelectors } from '../../../../app/auth/auth.selectors';
import { settingsSelectors } from '../../../../app/resource/settings/settings.selectors';

export const UserInfo: FunctionComponent<IUserInfoParams> = ({ selectedUserId }) => {
  const dispatch = useDispatch();

  const loggedUserId = useSelector(authSelectors.selectLoggedUser);
  const loggedUser = useSelectWithParams(userSelectors.selectResourceById, loggedUserId ? loggedUserId : '');
  const settings = useSelectWithParams(
    settingsSelectors.selectResourceById,
    loggedUser?.tenantId ? loggedUser.tenantId : '',
  );

  const selectedUser = useSelectWithParams(userSelectors.selectResourceById, selectedUserId) as IUser;
  const openUserForm = useCallback(() => {
    dispatch(
      openDialog('userFormDialog', {
        data: selectedUser ? selectedUser : undefined,
        onSubmit: (user: IUserUpdateForm, id?: IUser['id']) => {
          if (id) {
            dispatch(updateUser(id, { ...user, dob: moment(user.dob).format() }));
          }
        },
      }),
    );
  }, [dispatch, selectedUser]);

  const meetingIds = selectedUser?.teamIds;
  const meetings = useSelectWithParams(meetingSimpleSelectors.selectResourcesById, meetingIds ?? []);

  const accounts = selectedUser?.accountsnew;

  const showLoader = useSelectWithParams(pageStateSelectors.selectPageInProgress, 'users');

  return (
    selectedUser && (
      <div className="col-9 py-2 pr-3">
        <div className="border-radius-1 lightGray-bg p-2">
          <div className="panel px-5 py-2">
            <div className="no-hover">
              <div className="flex pb-3">
                <CircularAvatar
                  src={selectedUser.image}
                  title={`${selectedUser.firstName} ${selectedUser.lastName}`}
                  size="100"
                  classes="img-fluid bigger-avatar mr-1 mr-1"
                />
                <div className="avatar-desc">
                  <h2>{selectedUser.firstName}</h2>
                  <h2>{selectedUser.lastName}</h2>
                  <h3 className="blueGray light">
                    ATAK Interactive
                    {/*loggedUser.jobTitle*/}
                    <br />
                    {selectedUser.jobTitle}
                  </h3>
                  {selectedUser?.isAdmin === '1' && (
                    <a href="/" className="btn btn-md mx-0 blue-bg flex-inline mt-1 no-hover" title="Admin">
                      <img src={WhiteShieldIcon} alt="" className="img-fluid mr-05" /> Admin
                    </a>
                  )}
                </div>
              </div>
              {!!meetings?.length && (
                <div className="directory-item">
                  <div className="directory-item-left">Meetings: </div>
                  <div className="directory-item-right">
                    <i className="icon-user-full blueGray mr-1"></i>
                    {meetings &&
                      meetings.map(
                        (meeting) => meeting && <MeetingInfo selectedMeeting={meeting} key={`teamId-${meeting.id}`} />,
                      )}
                  </div>
                </div>
              )}
              {!!accounts?.length && (
                <div className="directory-item">
                  <div className="directory-item-left">Accounts:</div>
                  <div className="directory-item-right">
                    <i className="icon-users blueGray mr-1"></i>
                    {accounts &&
                      accounts.map((acc) => acc && <MeetingInfo selectedMeeting={acc} key={`teamId-${acc.id}`} />)}
                  </div>
                </div>
              )}
              {selectedUser.email && (
                <div className="directory-item">
                  <div className="directory-item-left">Email:</div>
                  <div className="directory-item-right flex">
                    <i className="icon2-at blueGray mr-1"></i>
                    <CopyToClipboard text={selectedUser.email} style={{ pointerEvents: 'auto' }}>
                      <span>{selectedUser.email}</span>
                    </CopyToClipboard>
                  </div>
                </div>
              )}
              {selectedUser.phone && (
                <div className="directory-item">
                  <div className="directory-item-left">Phone (work):</div>
                  <div className="directory-item-right flex">
                    <i className="icon2-phone blueGray mr-1"></i>
                    <CopyToClipboard text={selectedUser.phone} style={{ pointerEvents: 'auto' }}>
                      <span>{selectedUser.phone}</span>
                    </CopyToClipboard>
                  </div>
                </div>
              )}
              {selectedUser.alternativePhone && (
                <div className="directory-item">
                  <div className="directory-item-left">Phone (mobile):</div>
                  <div className="directory-item-right flex">
                    <i className="icon2-phone blueGray mr-1"></i>
                    <CopyToClipboard text={selectedUser.alternativePhone} style={{ pointerEvents: 'auto' }}>
                      <span>{selectedUser.alternativePhone}</span>
                    </CopyToClipboard>
                  </div>
                </div>
              )}
              {selectedUser.address && (
                <div className="directory-item">
                  <div className="directory-item-left">Address:</div>
                  <div className="directory-item-right flex">
                    <i className="icon2-location blueGray mr-1"></i>
                    <CopyToClipboard text={selectedUser.address} style={{ pointerEvents: 'auto' }}>
                      <span>{selectedUser.address}</span>
                    </CopyToClipboard>
                  </div>
                </div>
              )}
              {settings?.slackWorkspaceName ? (
                <div className="directory-item">
                  <div className="directory-item-left">Slack:</div>
                  <div className="directory-item-right flex">
                    {selectedUser?.slackId ? (
                      <span className="green">Synced</span>
                    ) : (
                      <span className="red">Not synced</span>
                    )}
                  </div>
                </div>
              ) : null}
              {/* {selectedUser.dob && (
                <div className="directory-item">
                  <div className="directory-item-left">Birthday:</div>
                  <div className="directory-item-right">
                    <i className="icon-calendar blueGray mr-1"></i>
                    <span>{formatDateUS(selectedUser.dob)}</span>
                  </div>
                </div>
              )} */}
            </div>
            {/* <div className="directory-item">
              <div className="directory-item-left">Slack:</div>
              <div className="directory-item-right">
                <i className="icon2-slack blueGray mr-1"></i>
                <a>{selectedUser.skype}</a>
              </div>
            </div> */}
            <div className="text-right flex aic mt-2 jcr">
              {showLoader ? (
                <div
                  style={{
                    float: 'right',
                    padding: '0px 10px',
                    marginTop: '10px',
                    height: '35px',
                    width: '80px',
                    justifyContent: 'center',
                  }}
                >
                  <Loader type="TailSpin" color="var(--theme-primary-color)" height="35px" width="35px" />
                </div>
              ) : (
                <>
                  <Link to={`/reports/users/${selectedUser.id}`} className="btn btn-md">
                    User Reports
                  </Link>
                  <a className="btn white-bg btn-border" title="Edit" onClick={openUserForm}>
                    Edit
                  </a>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );
};
